import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import { HandHeart, X, Check } from "@phosphor-icons/react";
import { usePaymentConfig, getPaymentOptionsForCurrency } from "@/hooks/usePaymentConfig";
import { useFxRates } from "@/hooks/useFxRates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_AMOUNTS = [10, 25, 50, 100, 250];

// Client-side tx_ref generation so the FW hook can bind a real ref from first render
function makeTxRef(prefix) {
  const rand = (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36)
  ).replace(/-/g, "");
  return `${prefix}-${rand}`;
}

const PRETTY_METHOD = {
  card: "Card",
  banktransfer: "Bank transfer",
  ussd: "USSD",
  mpesa: "M-Pesa",
  mobilemoneyghana: "Mobile money (Ghana)",
  mobilemoneyuganda: "Mobile money (Uganda)",
  mobilemoneyrwanda: "Mobile money (Rwanda)",
  mobilemoneyzambia: "Mobile money (Zambia)",
  mobilemoneyfranco: "Mobile money (Francophone)",
  gpay: "Google Pay",
  applepay: "Apple Pay",
};
const prettyMethod = (m) => PRETTY_METHOD[m] || m;

export default function DonateButton({ variant = "primary", label = "Donate" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="donate-button"
        className={
          variant === "primary"
            ? "inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            : "inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 text-sm font-medium"
        }
      >
        <HandHeart size={16} weight="fill" /> {label}
      </button>
      {open && <DonateModal onClose={() => setOpen(false)} />}
    </>
  );
}

export function DonateModal({ onClose, initialAmount = 25, initialCurrency = "", headline = "Support Conexus Foundation", description = "100% of donations go to satellite data costs, validator audits, and reforestation grants." }) {
  const { config } = usePaymentConfig();
  const { convert, loaded: fxLoaded } = useFxRates();
  const [amount, setAmount] = useState(initialAmount);
  const [currency, setCurrency] = useState(initialCurrency || "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [stage, setStage] = useState("form");
  const [error, setError] = useState("");

  // Amount is always USD (system base). User picks payment currency separately.
  // Default currency to merchant's primary (first in returned list, e.g. UGX for Ugandan merchants)
  useEffect(() => {
    if (!currency && config?.currencies?.length) {
      setCurrency(config.currencies[0]);
    }
  }, [config?.currencies, currency]);

  // Generate tx_ref ONCE per modal open so FW config binds a real ref from first render
  const txRef = useMemo(() => makeTxRef("donation"), []);

  // Filter payment_options to ones that work for the chosen currency on this merchant
  const filteredPaymentOptions = useMemo(
    () => getPaymentOptionsForCurrency(config, currency),
    [config, currency],
  );

  // Live-converted charged amount in the chosen currency
  const chargedAmount = useMemo(() => {
    if (!fxLoaded || !currency || currency === "USD") return amount;
    return convert(amount, "USD", currency);
  }, [amount, currency, fxLoaded, convert]);

  const flwConfig = useMemo(() => ({
    public_key: config?.public_key || "",
    tx_ref: txRef,
    amount: chargedAmount,
    currency: currency,
    payment_options: filteredPaymentOptions || "card",
    customer: {
      email: email || "donor@conexusfoundation.io",
      name: name || "Donor",
      phone_number: phone || "",
    },
    customizations: {
      title: "Conexus Foundation Donation",
      description: "Support earth monitoring and climate research",
      logo: "",
    },
  }), [config, txRef, chargedAmount, currency, email, name, phone, filteredPaymentOptions]);

  const handleFlutterwavePayment = useFlutterwave(flwConfig);

  const startPayment = () => {
    setError("");
    if (!amount || amount <= 0) return setError("Please enter a positive amount.");
    if (!name.trim()) return setError("Full name is required.");
    if (!email.trim()) return setError("Email is required.");
    if (!phone.trim()) return setError("Phone number is required.");
    if (!config?.public_key) return setError("Payment provider not configured.");
    if (!filteredPaymentOptions) {
      return setError(
        `Your Flutterwave account doesn't have a payment method enabled for ${currency}. ` +
        `Try a different currency (e.g. UGX, NGN, KES) or contact support.`,
      );
    }
    setStage("submitting");

    axios.post(`${API}/payments/donations/init`, {
      amount, currency, name, email, phone, tx_ref: txRef,
    }).then(() => {
      handleFlutterwavePayment({
        callback: async (payment) => {
          closePaymentModal();
          try {
            const v = await axios.post(`${API}/payments/donations/verify`, {
              tx_ref: txRef, transaction_id: payment.transaction_id,
            });
            setStage(v.data.status === "successful" ? "success" : "failed");
          } catch {
            setStage("failed");
          }
        },
        onClose: () => setStage("form"),
      });
    }).catch((e) => {
      setError(e.response?.data?.detail || "Could not start payment");
      setStage("form");
    });
  };

  return (
    <div className="fixed inset-0 z-[80] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" data-testid="donate-modal">
      <div className="bg-white rounded-2xl sm:rounded-3xl max-w-md w-full overflow-hidden max-h-[92vh] overflow-y-auto">
        <header className="flex items-center gap-3 p-4 sm:p-5 border-b border-stone-200 sticky top-0 bg-white z-10">
          <HandHeart size={20} weight="duotone" className="text-rose-600 shrink-0" />
          <h2 className="font-heading text-base sm:text-lg font-semibold text-stone-900 flex-1 truncate">{headline}</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="donate-close">
            <X size={18} weight="bold" />
          </button>
        </header>

        {stage === "success" ? (
          <div className="p-8 text-center" data-testid="donate-success">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check size={28} weight="bold" className="text-emerald-700" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-stone-900 mb-2">Thank you!</h3>
            <p className="text-sm text-stone-500 mb-5">Your donation of {currency} {amount} powers real climate research.</p>
            <button onClick={onClose} className="bg-emerald-700 text-white text-sm font-medium px-5 py-2 rounded-full">Close</button>
          </div>
        ) : stage === "failed" ? (
          <div className="p-8 text-center" data-testid="donate-failed">
            <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">Payment did not complete</h3>
            <p className="text-sm text-stone-500 mb-5">No charge was made. Please try again.</p>
            <button onClick={() => setStage("form")} className="bg-rose-600 text-white text-sm font-medium px-5 py-2 rounded-full">Try again</button>
          </div>
        ) : (
          <div className="p-4 sm:p-6 space-y-3">
            <p className="text-sm text-stone-500">{description}</p>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-400 block mb-2">Amount (USD)</label>
              <div className="grid grid-cols-5 gap-1.5 mb-2">
                {PRESET_AMOUNTS.map((a) => (
                  <button key={a} onClick={() => setAmount(a)}
                    className={`text-[11px] sm:text-xs py-2 rounded-lg border transition-colors ${
                      amount === a ? "bg-rose-600 border-rose-700 text-white" : "border-stone-200 text-stone-600 hover:border-rose-400"
                    }`}
                    data-testid={`donate-preset-${a}`}>
                    ${a}
                  </button>
                ))}
              </div>
              <div className="flex items-stretch gap-2">
                <div className="flex-1 flex items-center border border-stone-200 rounded-xl px-3 focus-within:border-rose-500">
                  <span className="text-sm text-stone-500 mr-1">US$</span>
                  <input type="number" min="1" step="1" value={amount}
                    onChange={(e) => setAmount(Number(e.target.value) || 0)}
                    className="flex-1 py-2.5 text-sm bg-transparent focus:outline-none"
                    data-testid="donate-amount" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[9px] uppercase tracking-wider text-stone-400 mb-0.5">Pay in</span>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                    className="px-3 py-2 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-rose-500"
                    data-testid="donate-currency">
                    {(config?.currencies || ["USD", "NGN", "GHS", "KES", "ZAR"]).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              {fxLoaded && currency && currency !== "USD" && amount > 0 && (
                <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 mt-2" data-testid="donate-fx-equiv">
                  You&apos;ll pay <strong>{currency} {chargedAmount.toLocaleString()}</strong> at live rate · (US${amount} base)
                </p>
              )}
            </div>

            <input type="text" placeholder="Full name *" value={name} required
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-rose-500"
              data-testid="donate-name" />
            <input type="email" placeholder="Email *" value={email} required
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-rose-500"
              data-testid="donate-email" />
            <input type="tel" placeholder="Phone number * (e.g. +234...)" value={phone} required
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-rose-500"
              data-testid="donate-phone" />

            {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="donate-error">{error}</p>}
            {filteredPaymentOptions && (
              <p className="text-[10px] text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" data-testid="donate-methods-hint">
                For <strong>{currency}</strong>, available methods: {filteredPaymentOptions.split(",").map(prettyMethod).join(", ")}.
              </p>
            )}
            {currency && !filteredPaymentOptions && (
              <p className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                No payment method is enabled for {currency} on this account. Please choose a different currency.
              </p>
            )}
            {config?.mode === "test" && (
              <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                TEST MODE — no real charges. Use Flutterwave test card 4187 4274 1556 4246, CVV 828, Exp 09/32, PIN 3310, OTP 12345.
              </p>
            )}

            <button
              onClick={startPayment}
              disabled={stage === "submitting" || !config?.public_key}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium py-3 rounded-xl disabled:bg-stone-400 transition-colors flex items-center justify-center gap-2"
              data-testid="donate-pay-btn"
            >
              {stage === "submitting" ? "Loading..." : <><HandHeart size={14} weight="fill" /> Donate {currency} {amount}</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
